'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@ironhack/design-system2/components/box';
import { Button } from '@ironhack/design-system2/components/button';
import { Flex } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { Text } from '@ironhack/design-system2/components/text';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';
import { type ReactElement, useEffect, useState } from 'react';
import { Image, StructuredTextGraphQlResponse } from 'react-datocms';
import { FormProvider, useForm } from 'react-hook-form';

import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { StructuredContent } from '@/app/components/client/structured-content';
import { TextField } from '@/app/components/client/text-field';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { useFormRequest } from '@/hooks/use-form-request';
import { type DatoBlock, type DatoImage } from '@/lib/datocms/types';
import { sendEvent } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import { getNurturingSchema } from '@/lib/utils/form-validations/forms/nurturing-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';

type FormData = {
  cToken: string;
} & EmailInput;

export type Header = DatoBlock<{
  buttonText: string;
  emailPlaceholder: string;
  emailValidationMessages: ValidationMessageSet;
  image: DatoImage;
  legalText: StructuredTextGraphQlResponse;
  salesforceSource: string;
  submitError: string;
  submitSuccess: string;
  subtitle: StructuredTextGraphQlResponse;
  title: string;
}>;

type Props = { data: Header };

const eventBase = { eventCategory: 'Blog', eventAction: 'subscribe' };

const events = {
  subscribeClick: (): void =>
    sendEvent({ ...eventBase, eventLabel: 'home - click subscribe' }),
  subscribeSuccess: (): void =>
    sendEvent({ ...eventBase, eventLabel: 'home - successfully subscribe' }),
  subscribeFail: (): void =>
    sendEvent({ ...eventBase, eventLabel: 'home - subscribe failed' }),
};

export const Header = (props: Props): ReactElement => {
  const { data } = props;
  const {
    buttonText,
    emailPlaceholder,
    image,
    legalText,
    salesforceSource,
    submitError,
    submitSuccess,
    subtitle,
    title,
  } = data;

  const {
    params: { language },
  } = usePageContext();

  const imageLayout = useBreakpointValue<'intrinsic' | 'fixed'>({
    base: 'intrinsic',
    lg: 'fixed',
  });

  const formSchema = getNurturingSchema(data);
  const formMethods = useForm<FormData>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error' | 'success';
    message: string;
  } | null>();
  const formRequest = useFormRequest();
  const setCaptchaToken = (token: string) => setValue('cToken', token);
  const cToken = watch('cToken');

  useEffect(() => {
    if (isDirty && formFeedback?.status === 'success') {
      setFormFeedback(null);
    }
  }, [formFeedback?.status, isDirty]);

  const onSubmit = async (formData: FormData): Promise<void> => {
    events.subscribeClick();
    setFormFeedback(null);
    try {
      await formRequest({
        data: {
          ...formData,
          emailOptIn: true,
          language,
          source: salesforceSource,
        },
        formType: 'nurturing',
      });

      events.subscribeSuccess();
      setFormFeedback({
        status: 'success',
        message: submitSuccess,
      });
      reset();
    } catch {
      events.subscribeFail();
      setFormFeedback({
        status: 'error',
        message: submitError,
      });
    }
  };

  return (
    <ModuleWrapper
      innerProps={{
        direction: 'column',
        justifyContent: ['space-between', null, 'center'],
        minH: ['666px', null, 'auto'],
        p: 0,
        position: 'relative',
        w: 'full',
      }}
      moduleFormat={{
        background: 'transparent',
        spacingBottom: '0',
        spacingTop: '0',
      }}
      outerProps={{ as: 'header', h: 'full', overflow: 'hidden' }}
    >
      {/* Small z-index to ensure it's over the image in smaller screens */}
      <Box px={[2, null, 0]} zIndex={[1, null, null, null, 'auto']}>
        <Text
          as="h1"
          color="text.inverse"
          mt={[3, null, 11]}
          textStyle={['5xl', null, '7xl']}
        >
          {title}
        </Text>
        <StructuredContent
          color="text.inverse"
          data={subtitle}
          mt={2}
          textStyle="l"
          w={['full', null, '480px']}
        />
      </Box>
      <Flex
        bg="background.white"
        borderRadius={['16px 16px 0 0', null, '16px']}
        flexDirection="column"
        mb={[null, null, 10]}
        minH="145px"
        mt={4}
        px={2}
        py={3}
        w={['full', null, '480px']}
        // Small z-index to ensure it's over the image in smaller screens
        zIndex={[1, null, null, null, 'auto']}
      >
        <FormProvider {...formMethods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <TextField
              error={errors.email?.message}
              isRequired
              name="email"
              placeholder={emailPlaceholder}
              type="email"
            />
            {!cToken && (
              <TurnstileFormToken
                language={language}
                onVerify={setCaptchaToken}
              />
            )}
            <Button isLoading={isSubmitting} mt={2} type="submit" w="full">
              {buttonText}
            </Button>
            <StructuredContent
              color="text.secondary"
              data={legalText}
              mt={1}
              textAlign="center"
              textStyle="m"
            />
            {formFeedback?.status && (
              <FormFeedback mt={2} {...formFeedback} id="blogHomeLeadSuccess" />
            )}
          </form>
        </FormProvider>
      </Flex>
      <Box
        bottom={['116px', null, 0]}
        position="absolute"
        right={['50%', null, 0]}
        transform={['translate(50%)', null, 'none']}
        w={['full', null, 'auto']}
      >
        <Image data={image.responsiveImage} layout={imageLayout} />
      </Box>
    </ModuleWrapper>
  );
};
